import {
  EventFeedEvent,
  EventFeedEventV3,
  EventFeedRequestV2,
  EventFeedRequestV3,
  EventFeedResponse,
  EventFeedResponseV3,
  EventFeedType,
} from '@api/api/event-feed';
import { KgInsightCategory } from '@api/api/kg-insights';
import { MarketOverview, marketOverview } from '@api/api/market-overview';
import { wretchRequest } from '@terminal/utils/api-fetch/apiFetch';
import { format, sub } from 'date-fns';
import { t } from 'i18next';

const MARKET_OVERVIEW_PERIOD_DAYS = 15;

export const fetchMarketOverviewInsights = async (
  pageNumber: number,
  signal?: AbortSignal
): Promise<MarketOverview> => {
  const daysToSubtract = MARKET_OVERVIEW_PERIOD_DAYS * pageNumber;
  const fromDate = format(
    sub(new Date(), { days: daysToSubtract }),
    'yyyy-MM-dd'
  );

  return wretchRequest<MarketOverview>(
    marketOverview.getMarketOverview.path({ fromDate }),
    {
      schema: marketOverview.getMarketOverview.schema,
      signal,
    }
  );
};

const EVENT_FEED_CATEGORY_TO_TYPE: Record<KgInsightCategory, EventFeedType> = {
  company_catalyst: 'attribution-insight',
  market_catalyst: 'attribution-insight',
  earnings_preview: 'earnings-insight',
  earnings_recap: 'earnings-insight',
};

// TODO: Remove this function when the event feed is completely migrated to v3
// this function is used to transform v2 event feed body to v3 event feed body
export const transformEventFeedBody = (
  body: EventFeedRequestV2
): EventFeedRequestV3 => {
  const filters = body?.filters ?? {};
  const categories: Set<string> = new Set();

  if ('company' in filters) {
    categories.add('company_catalyst');
  } else if ('market' in filters) {
    categories.add('market_catalyst');
  }

  if (body.type === 'earnings-insight') {
    if (!body.filters) {
      categories.add('earnings_recap');
      categories.add('earnings_preview');
    } else {
      body.filters.type.values.forEach(item => {
        item.earnings_type === 'recap'
          ? categories.add('earnings_recap')
          : categories.add('earnings_preview');
      });
    }
  }

  // add a fallback for attribution-insight if no categories are selected
  if (categories.size === 0 && body.type == 'attribution-insight') {
    categories.add('company_catalyst');
    categories.add('market_catalyst');
  }

  const mapTypeToEventFeedType = {
    'market-overview': 'market-overview',
    'scenario-insight': 'scenario-insight',
    'earnings-insight': 'kg-insight',
    'attribution-insight': 'kg-insight',
  };

  const finalType =
    body.type &&
    (mapTypeToEventFeedType[body.type] as EventFeedRequestV3['type']);
  return {
    ...body,
    filters: body.type === 'earnings-insight' ? undefined : body.filters,
    categories: categories.size > 0 ? Array.from(categories) : undefined,
    type: finalType,
  };
};

const isKgInsightEvent = (event: EventFeedEventV3): event is EventFeedEventV3 =>
  event.type === 'kg-insight';

// TODO: Remove this function when the event feed is completely migrated to v3
// this function is used to map v3 events to v2 events format
// since many places rely on v2 event feed response payload shape
const mapEventFeedV3Event = (event: EventFeedEventV3) => {
  const metadataCategory = event.metadata.category;
  const type = EVENT_FEED_CATEGORY_TO_TYPE[metadataCategory] ?? event.type;

  const mapSubjectTypeToSubjectRefId = {
    theme: undefined,
    geo: event.metadata.subject_ref_id,
    entity: event.metadata.subject_id,
  };

  return {
    ...event,
    type,
    _type: event.type,
    entity:
      event.entity ||
      (event.metadata.subject_type &&
        mapSubjectTypeToSubjectRefId[event.metadata.subject_type]),
    metadata: {
      ...event.metadata,
      action_type: t(
        `insights:catalyst.subcategories.${event.metadata.sub_category}`
      ),
      earnings_type:
        event.metadata.category === 'earnings_preview'
          ? 'preview'
          : event.metadata.category === 'earnings_recap'
            ? 'recap'
            : undefined,
      earnings_id: event.metadata.subject_ref_id,
      identifier:
        event.metadata.sub_category === 'thematic'
          ? (event.metadata.subject_id as string)
          : event.metadata.identifier,
      category:
        metadataCategory === 'market_catalyst'
          ? 'market_wide'
          : metadataCategory,
    },
  } as EventFeedEvent;
};

export const mapEventFeedResponse = (
  response: EventFeedResponseV3
): EventFeedResponse => ({
  ...response,
  events: response.events.map(event =>
    isKgInsightEvent(event) ? mapEventFeedV3Event(event) : event
  ),
});

export const enrichEventsWithMarketOverviewInsights = (
  events: EventFeedEvent[],
  marketOverviewData?: MarketOverview
): EventFeedEvent[] => {
  if (!marketOverviewData?.summaries?.length) {
    return events;
  }

  const summariesMap = new Map(
    marketOverviewData.summaries.map(summary => [summary.id, summary])
  );

  return events.map(event => {
    if (event.type !== 'market-overview') {
      return event;
    }

    const matchingSummary = summariesMap.get(event.source_id);
    if (!matchingSummary) {
      return event;
    }

    return {
      ...event,
      summary: matchingSummary.summary,
      metadata: {
        ...event.metadata,
        questions: matchingSummary.questions,
      },
    };
  });
};
